import {getSSRContext} from '@bitstillery/mithril'
import {is_nested_object, property_of} from '@bitstillery/common/lib/utils'

/**
 * SSR→client data handoff: the server records each keyed value into `__SSR_STATE__.__meta.ssr_data`;
 * the browser seeds from it once on boot so the first render reuses the data instead of refetching.
 *
 * It is deliberately not a reactive Mithril state — it is a one-shot boot snapshot. This is the wire
 * underneath `cached()` (see `api_cache.ts`), which records/reads these on behalf of callers; prefer
 * `cached()` over using `record_ssr_data`/`read_ssr_data` directly.
 */

let seeded = false
const snapshot = new Map<string, unknown>()

function seed_from_ssr_state(): void {
    // Server has no snapshot to seed from (it produces one); only the browser reads it, once.
    if (seeded || globalThis.__SSR_MODE__) {
        return
    }
    seeded = true
    const data = property_of(property_of(property_of(globalThis, '__SSR_STATE__'), '__meta'), 'ssr_data')
    if (is_nested_object(data)) {
        for (const [key, value] of Object.entries(data)) {
            snapshot.set(key, value)
        }
    }
}

/** The server-rendered value for `key` (browser only), or `undefined` when there is none. */
export function read_ssr_data(key: string): unknown {
    seed_from_ssr_state()
    return snapshot.get(key)
}

/** Record `data` so the browser can reuse it after hydration. No-op outside SSR. */
export function record_ssr_data(key: string, data: unknown): void {
    if (!globalThis.__SSR_MODE__) {
        return
    }
    const ctx = getSSRContext()
    if (!ctx) {
        return
    }
    const recorded = ctx.ssrStateMeta?.ssr_data
    const ssr_data = {...(is_nested_object(recorded) ? recorded : {}), [key]: data}
    ctx.ssrStateMeta = {...(ctx.ssrStateMeta ?? {}), ssr_data}
}
